<template>
  <main class="embed-shell">
    <div v-if="state === 'loading'" class="embed-state">组件正在加载...</div>
    <div v-else-if="state === 'error'" class="embed-state embed-error" data-test="embed-error">
      {{ errorMessage }}
    </div>
    <div v-else-if="state === 'waiting'" class="embed-state">等待安全令牌...</div>
    <div v-else class="embed-content" data-test="embed-ready">
      <waybill-map-page
        v-if="activeWaybillNo"
        :embed-waybill-no="activeWaybillNo"
        embed-mode
        @close="closeWaybill"
      />
      <full-map
        v-else-if="component === 'full-map'"
        ref="businessComponent"
        embed-mode
        @open-waybill="openWaybill"
        @vehicle-selected="payload => postEvent('vehicleSelected', payload)"
      />
      <in-transit
        v-else-if="component === 'waybill-list'"
        ref="businessComponent"
        embed-mode
        :action-scopes="actionScopes"
        @open-waybill="openWaybill"
        @waybill-selected="payload => postEvent('waybillSelected', payload)"
        @action-completed="payload => postEvent('actionCompleted', payload)"
      />
      <waybill-map-page
        v-else
        ref="businessComponent"
        :embed-waybill-no="defaultWaybillNo"
        embed-mode
        @close="postEvent('waybillSelected', { waybillNo: defaultWaybillNo })"
      />
    </div>
  </main>
</template>

<script>
import axios from 'axios'
import FullMap from '@/views/in-transit/FullMap.vue'
import InTransit from '@/views/in-transit/InTransit.vue'
import WaybillMapPage from '@/views/in-transit/WaybillMapPage.vue'
const protocol = require('./protocol')
const embedClient = require('./embedClient')

export default {
  name: 'EmbedShell',
  components: { FullMap, InTransit, WaybillMapPage },
  data() {
    return {
      component: this.$route.meta.embedComponent,
      state: 'loading',
      errorMessage: '',
      token: '',
      actionScopes: [],
      defaultWaybillNo: '',
      activeWaybillNo: '',
      session: null,
      allowedOrigin: '',
      resizeObserver: null,
      expirationTimer: null
    }
  },
  async mounted() {
    await this.initialize()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.expirationTimer) window.clearTimeout(this.expirationTimer)
    embedClient.clearEmbedToken()
    this.token = ''
  },
  methods: {
    async initialize() {
      try {
        const sessionId = this.$route.query.embedSessionId
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        const handshakeId = fragment.get('handshakeId')
        if (!sessionId || !handshakeId) throw new Error('组件启动参数不完整')
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API || ''}/embed/session/${encodeURIComponent(sessionId)}`,
          { timeout: 10000 }
        )
        const metadata = response.data
        if (!metadata || metadata.component !== this.component) {
          throw new Error('组件会话与页面不匹配')
        }
        this.session = protocol.createEmbedSession({
          parentWindow: window.parent,
          allowedOrigin: metadata.allowedOrigin,
          handshakeId,
          onMessage: this.acceptMessage
        })
        this.allowedOrigin = this.session.getAllowedOrigin()
        window.addEventListener('message', this.handleMessage)
        this.state = 'waiting'
        this.post(protocol.MESSAGE_TYPES.READY, { component: this.component })
        this.observeSize()
      } catch (error) {
        this.fail(error && error.message ? error.message : '组件加载失败')
      }
    },
    handleMessage(event) {
      if (this.session) this.session.accept(event)
    },
    acceptMessage(message) {
      if (message.type === protocol.MESSAGE_TYPES.TOKEN) {
        if (typeof message.token !== 'string' || !message.token.trim()) {
          this.fail('组件令牌不正确')
          return
        }
        this.token = message.token
        embedClient.setEmbedToken(message.token)
        const claims = embedClient.readTokenClaims(message.token)
        if (claims.component && claims.component !== this.component) {
          this.fail('组件令牌与页面不匹配')
          return
        }
        this.actionScopes = Array.isArray(claims.actionScopes) ? claims.actionScopes : []
        this.defaultWaybillNo = Array.isArray(claims.waybillNos)
          ? (claims.waybillNos[0] || '') : ''
        if (this.component === 'waybill-map' && !this.defaultWaybillNo) {
          this.fail('组件令牌未指定运单')
          return
        }
        this.scheduleExpiration(claims.exp)
        this.state = 'ready'
        this.$emit('token', message.token)
      } else if (message.type === protocol.MESSAGE_TYPES.COMMAND) {
        this.executeCommand(message)
        this.$emit('command', message)
      }
    },
    post(type, payload) {
      if (!this.session || !this.allowedOrigin) return
      window.parent.postMessage(this.session.message(type, payload), this.allowedOrigin)
    },
    observeSize() {
      if (typeof ResizeObserver === 'undefined') return
      this.resizeObserver = new ResizeObserver(entries => {
        const rect = entries[0] && entries[0].contentRect
        if (rect) this.post(protocol.MESSAGE_TYPES.RESIZE, {
          width: Math.ceil(rect.width),
          height: Math.ceil(rect.height)
        })
      })
      this.resizeObserver.observe(this.$el)
    },
    scheduleExpiration(exp) {
      if (this.expirationTimer) window.clearTimeout(this.expirationTimer)
      const delay = Number(exp) * 1000 - Date.now()
      if (!Number.isFinite(delay)) return
      this.expirationTimer = window.setTimeout(() => {
        embedClient.clearEmbedToken()
        this.token = ''
        this.state = 'waiting'
        this.postEvent('tokenExpired', { expiredAt: new Date(Number(exp) * 1000).toISOString() })
      }, Math.max(0, Math.min(delay, 2147483647)))
    },
    openWaybill(waybillNo) {
      if (!waybillNo) return
      this.activeWaybillNo = waybillNo
      this.postEvent('waybillSelected', { waybillNo })
    },
    closeWaybill() {
      this.activeWaybillNo = ''
    },
    postEvent(eventName, payload) {
      this.post(protocol.MESSAGE_TYPES.EVENT, { eventName, payload })
    },
    executeCommand(message) {
      if (message.command === 'focusWaybill' && message.payload && message.payload.waybillNo) {
        this.openWaybill(message.payload.waybillNo)
        return
      }
      const component = this.$refs.businessComponent
      if (component && typeof component.applyEmbedCommand === 'function') {
        component.applyEmbedCommand(message.command, message.payload || {})
      }
    },
    fail(message) {
      embedClient.clearEmbedToken()
      this.token = ''
      this.errorMessage = message
      this.state = 'error'
    }
  }
}
</script>

<style scoped>
.embed-shell, .embed-content {
  width: 100%;
  height: 100%;
}

.embed-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #606266;
  background: #f5f7fa;
}

.embed-error {
  color: #f56c6c;
}
</style>
